import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  NavigationMenuViewport,
} from "@/components/ui/navigation-menu";
import { NavDrawer } from "@/components/shared/NavDrawer";
import { TRADES } from "@/lib/for-trades";

interface NavItem {
  type: "item";
  title: string;
  href: string;
  description?: string; // (optional, shown in desktop dropdowns)
}
interface NavSection {
  type: "section";
  title: string;
  items: NavItem[];
  /** Render the dropdown's items as a multi-column grid. Defaults to
   *  one column; bump to 2 or 3 for sections with lots of links so
   *  the menu doesn't grow taller than the viewport. */
  columns?: 1 | 2 | 3;
}
type NavItems = (NavItem | NavSection)[];

// Pull the trade list from the same source the /for/<slug> pages use,
// so adding a new TradeContent entry in for-trades.ts auto-shows the
// link in the main nav -- no second list to keep in sync. The
// "all-trades" catch-all is hoisted to the top of the dropdown so a
// visitor whose trade isn't on the list sees it first.
const tradeNavItems: NavItem[] = [
  ...TRADES.filter((t) => t.slug === "all-trades"),
  ...TRADES.filter((t) => t.slug !== "all-trades"),
].map((t) => ({
  type: "item",
  title: t.plural,
  href: `/for/${t.slug}`,
}));

const navigationItems: NavItems = [
  // "Your trade" lifts the per-trade landing pages into the main nav
  // so visitors can self-select before reading the generic homepage
  // copy. Single source of truth: TRADES from for-trades.ts.
  {
    type: "section",
    title: "Your trade",
    items: tradeNavItems,
    columns: 2,
  },
  { type: "item", title: "Features", href: "/#features" },
  { type: "item", title: "Pricing", href: "/#pricing" },
  { type: "item", title: "FAQ", href: "/#faq" },
];

// Root navigation bar: logo, mobile, and desktop nav
export const Navigation = () => (
  <div className="flex justify-center items-center gap-2">
    <MobileNav />
    <div className="flex-shrink-0">
      <Link to="/" className="flex items-center gap-2">
        <svg className="h-7 w-7" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path transform="translate(0,-3)" d="M 92 8 L 92 38 C 92 46 86 52 78 52 L 50 52 C 44 52 40 48 40 42 L 40 28 C 40 18 48 8 58 8 Z" fill="#f59e0b"/>
          <path transform="translate(0,3)" d="M 8 92 L 8 62 C 8 54 14 48 22 48 L 50 48 C 56 48 60 52 60 58 L 60 72 C 60 82 52 92 42 92 Z" fill="#f59e0b"/>
        </svg>
        <span className="font-bold text-base">Manos</span>
      </Link>
    </div>
    <DesktopNav />
  </div>
);

// Mobile hamburger menu, uses Sheet for slide-out drawer
const MobileNav = () => {
  return (
    <div className="md:hidden">
      <NavDrawer>
        {({ close }) => (
          <nav className="flex flex-col gap-4 px-6 pt-16 pb-8">
            {navigationItems.map((item) =>
              item.type === "section" ? (
                <div key={item.title}>
                  <p className="text-sm font-medium">{item.title}</p>
                  <div className="mt-2 flex flex-col gap-2 pl-4">
                    {item.items.map((subItem) => (
                      <Link
                        key={subItem.title}
                        to={subItem.href}
                        className="text-sm text-gray-600 hover:text-gray-900"
                        onClick={close}
                      >
                        {subItem.title}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  key={item.title}
                  to={item.href}
                  className="text-sm font-medium hover:text-gray-900"
                  onClick={close}
                >
                  {item.title}
                </Link>
              )
            )}
          </nav>
        )}
      </NavDrawer>
    </div>
  );
};

// Desktop horizontal nav, supports dropdown sections
const DesktopNav = () => (
  <NavigationMenu className="hidden md:flex px-4">
    <NavigationMenuList className="flex gap-1">
      {navigationItems.map((item) => (
        <NavigationMenuItem key={item.title}>
          {item.type === "item" ? (
            <NavigationMenuLink asChild>
              <Button className="px-3" variant="ghost">
                <Link to={item.href}>{item.title}</Link>
              </Button>
            </NavigationMenuLink>
          ) : (
            <>
              <NavigationMenuTrigger className="px-3 py-2 text-sm font-medium transition-colors hover:text-accent-foreground">
                {item.title}
              </NavigationMenuTrigger>
              <NavigationMenuContent
                className={
                  item.columns && item.columns >= 2
                    ? "min-w-[420px] p-2"
                    : "min-w-[200px] p-2"
                }
              >
                <div className="p-2">
                  <div
                    className={
                      item.columns === 3
                        ? "grid grid-cols-3 gap-1"
                        : item.columns === 2
                          ? "grid grid-cols-2 gap-1"
                          : "grid grid-cols-1"
                    }
                  >
                    {item.items.map((subItem) => (
                      <NavigationMenuLink key={subItem.title} asChild>
                        <Link
                          to={subItem.href}
                          className="block flex flex-col gap-1 rounded-md p-3 transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                        >
                          <div className="text-sm font-medium">{subItem.title}</div>
                          {subItem.description && (
                            <p className="text-sm text-muted-foreground">{subItem.description}</p>
                          )}
                        </Link>
                      </NavigationMenuLink>
                    ))}
                  </div>
                </div>
              </NavigationMenuContent>
            </>
          )}
        </NavigationMenuItem>
      ))}
    </NavigationMenuList>
    <NavigationMenuViewport />
  </NavigationMenu>
);

// To extend: add to navigationItems. For custom rendering, edit MobileNav/DesktopNav.
